import React, { useEffect, useState, useRef } from "react";
import { AtSign, Camera, Loader2, Check, X, Info, Globe, Hash, Euro, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, Field, Segmented } from "../components/ui";
import Avatar from "../components/Avatar";
import CompletionChecklist from "../components/CompletionChecklist";
import NotificationPrefs from "../components/NotificationPrefs";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";
import { useTranslation } from "react-i18next";
import { useMoney } from "../context/MoneyContext";
import { useAuth } from "../context/AuthContext";
import { CURRENCIES } from "../components/ui";

/**
 * Public identity: username, picture, and (for members) which of the two appears on
 * comments. Investors don't get the persona choice — their full name is always
 * shown, because people are being asked to follow their money decisions.
 */
export default function Identity() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [username, setUsername] = useState("");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const { homeCurrency, setHomeCurrency, language, setLanguage, numberLocale, setNumberLocale } = useMoney();
  const { features } = useAuth();
  const fileRef = useRef();
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("XX");

  useEffect(() => {
    api.countries().then((r) => setCountries(r.data.countries || [])).catch(() => {});
    api.me().then((r) => setCountry(r.data.country || "XX")).catch(() => {});
  }, []);

  // Changing country changes the currency and number format the whole product uses,
  // so the fresh values are pushed straight back into MoneyContext rather than
  // waiting for a reload.
  const saveCountry = async (code) => {
    setCountry(code);
    try {
      const { data } = await api.updateSettings({ country: code });
      if (data.home_currency) setHomeCurrency(data.home_currency);
      if (data.number_locale) setNumberLocale(data.number_locale);
      toast.success(t("identity.countrySaved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  useSeo({ title: t("identity.title"), noindex: true });

  const load = () => api.profile().then((r) => { setData(r.data); setUsername(r.data.username || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);

  // Live availability, debounced — telling someone their name is taken after they
  // hit save is a worse experience than telling them as they type.
  useEffect(() => {
    if (!username || username === data?.username) { setCheck(null); return; }
    const timer = setTimeout(() => {
      api.checkUsername(username).then((r) => setCheck(r.data)).catch(() => setCheck(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [username, data?.username]);

  const saveUsername = async () => {
    setBusy(true);
    try { await api.setUsername(username); toast.success(t("identity.usernameSaved")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t("identity.tooBig")); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try { await api.setAvatar(reader.result); toast.success(t("identity.avatarSaved")); load(); }
      catch (er) { toast.error(er.response?.data?.detail || t("dialogs.failed")); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const removeAvatar = async () => {
    try { await api.clearAvatar(); load(); } catch { toast.error(t("dialogs.failed")); }
  };

  const setPersona = async (mode) => {
    try { await api.setCommentAs(mode); toast.success(t("dialogs.saved")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-32" /></div>;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <span className="label">{t("identity.subtitle")}</span>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("identity.title")}</h1>
      </div>

      <CompletionChecklist completion={data.completion} />

      <Panel className="card p-5 sm:p-6" testid="identity-avatar">
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("identity.pictureTitle")}</h2>
        <p className="text-sm text-slate-500 mt-1">
          {data.is_investor ? t("identity.pictureBodyInvestor") : t("identity.pictureBody")}
        </p>
        <div className="mt-4 flex items-center gap-4">
          <Avatar src={data.avatar} name={data.identity.display_name} size={72} testid="my-avatar" />
          <div className="flex flex-wrap gap-2">
            <button className="btn-outline" onClick={() => fileRef.current?.click()} disabled={busy}
                    data-testid="upload-avatar">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
              {t("identity.upload")}
            </button>
            {data.avatar && (
              <button className="btn-ghost text-slate-500" onClick={removeAvatar} data-testid="remove-avatar">
                <X size={15} /> {t("common.delete")}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                   className="hidden" onChange={pickFile} data-testid="avatar-input" />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3 flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 shrink-0" /> {t("identity.pictureNote")}
        </p>
      </Panel>

      <Panel className="card p-5 sm:p-6" testid="identity-username">
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("identity.usernameTitle")}</h2>
        <p className="text-sm text-slate-500 mt-1">{t("identity.usernameBody")}</p>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <div className="flex items-center flex-1 rounded-xl border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-brand-500">
            <AtSign size={15} className="ml-3 text-slate-400 shrink-0" />
            <input className="flex-1 py-2.5 px-2 outline-none text-sm" data-testid="username-input"
                   value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())}
                   placeholder={t("identity.usernamePlaceholder")} />
            {check && (
              <span className="pr-3 shrink-0">
                {check.available ? <Check size={16} className="text-emerald-500" />
                                 : <X size={16} className="text-rose-500" />}
              </span>
            )}
          </div>
          <button className="btn-primary" onClick={saveUsername}
                  disabled={busy || !username || username === data.username || (check && !check.available)}
                  data-testid="save-username">
            {t("common.save")}
          </button>
        </div>
        {check && !check.available && check.reason && (
          <p className="text-xs text-rose-600 mt-2" data-testid="username-error">{check.reason}</p>
        )}
        <p className="text-[11px] text-slate-400 mt-2">{t("identity.usernameLimit")}</p>
      </Panel>

      {/* Preferences moved here from the sidebar: they're settings, not navigation,
          and burying a currency switch in a nav rail is how people miss it. */}
      <Panel className="card p-5 sm:p-6" testid="identity-preferences">
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("identity.prefsTitle")}</h2>
        <p className="text-sm text-slate-500 mt-1">{t("identity.prefsBody")}</p>

        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <MapPin size={16} className="text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{t("identity.country")}</div>
                <div className="text-xs text-slate-400">{t("identity.countryHint")}</div>
              </div>
            </div>
            <select className="input py-2 w-auto" data-testid="pref-country"
                    value={country} onChange={(e) => saveCountry(e.target.value)}>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {i18n.language === "de" ? c.name_de : c.name_en}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <Euro size={16} className="text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{t("common.homeCurrency")}</div>
                <div className="text-xs text-slate-400">{t("identity.currencyHint")}</div>
              </div>
            </div>
            <select className="input py-2 w-auto" data-testid="pref-currency"
                    value={homeCurrency} onChange={(e) => setHomeCurrency(e.target.value)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <Globe size={16} className="text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{t("common.language")}</div>
                <div className="text-xs text-slate-400">{t("identity.languageHint")}</div>
              </div>
            </div>
            <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="pref-language">
              {[["en", "EN"], ["de", "DE"]].map(([code, lbl]) => (
                <button key={code} data-testid={`lang-${code}`} onClick={() => setLanguage(code)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                          language === code ? "bg-white text-brand-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <Hash size={16} className="text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{t("common.numberFormat")}</div>
                <div className="text-xs text-slate-400">{t("identity.numberHint")}</div>
              </div>
            </div>
            <select className="input py-2 w-auto tabular" data-testid="pref-number"
                    value={numberLocale} onChange={(e) => setNumberLocale(e.target.value)}>
              <option value="en-US">1,234.56</option>
              <option value="de-DE">1.234,56</option>
            </select>
          </div>
        </div>
      </Panel>

      {/* How you appear in comments — only meaningful where there are comments. */}
      {features.community && (
      <Panel className="card p-5 sm:p-6" testid="identity-persona">
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("identity.personaTitle")}</h2>
        {data.is_investor ? (
          <div className="mt-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-600 leading-relaxed">
            {t("identity.personaInvestor", { name: data.full_name })}
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mt-1">{t("identity.personaBody")}</p>
            <div className="mt-4">
              <Segmented
                options={[
                  { value: "username", label: username ? `@${username}` : t("identity.username") },
                  { value: "name", label: data.full_name },
                ]}
                value={data.comment_as} onChange={setPersona} testid="persona-toggle" fullWidthOnMobile
              />
            </div>
            <div className="mt-4 flex items-center gap-3 rounded-xl bg-slate-50 p-3.5">
              <Avatar src={data.avatar} name={data.identity.display_name} size={36} />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{data.identity.display_name}</div>
                <div className="text-xs text-slate-400">{t("identity.previewNote")}</div>
              </div>
            </div>
          </>
        )}
      </Panel>
      )}

      <NotificationPrefs />
    </div>
  );
}
